import {startOfToday} from 'date-fns';
import {closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {defaultTo, isNil} from 'ramda';

import {
  CoreExchangeReturnToSupplierTab,
  usePatchCoreExchangeSettingsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, useGetVatRatesOptions} from '@dms/shared';

import {
  getApiDateTimeString,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {DialogActions} from './DialogActions';

const ReturnSettingsFormSchema = z.object({
  returnDeadline: z.date(),
  returnTransactionType: z.string().min(1),
  returnIsVehicleRequired: z.boolean(),
  returnUnitPrice: z.number(),
  returnVatRate: z.string().min(1),
  returnDiscount: z.number(),
  returnProcessingFee: z.number(),
  returnVatValue: z.number(),
  returnSupplierDicountGroupCode: z.string(),
  returnTotalValueWithoutVat: z.number(),
  returnTotalValueWithVat: z.number(),
});

type ReturnSettingsForm = z.infer<typeof ReturnSettingsFormSchema>;

const GRID_COLUMNS = 2;
const PRICE_DECIMALS = 2;

interface ReturnSettingsDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  settings: CoreExchangeReturnToSupplierTab['settings'] | Nullish;
  onSaved: VoidFunction;
}

export function ReturnSettingsDialog(props: ReturnSettingsDialogProps) {
  const [patchSettings, {isLoading}] = usePatchCoreExchangeSettingsMutation();
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const settings = props.settings;

  const handleSubmit = async (values: ReturnSettingsForm) => {
    await patchSettings({
      coreExchangeId: props.coreExchangeId,
      body: {...values, returnDeadline: getApiDateTimeString(values.returnDeadline)},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<ReturnSettingsForm>
      experimentalZodSchema={ReturnSettingsFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        returnDeadline: isNil(settings?.returnDeadline)
          ? startOfToday()
          : parseDate(settings.returnDeadline),
        returnTransactionType: defaultTo('', settings?.returnTransactionType),
        returnIsVehicleRequired: defaultTo(false, settings?.returnIsVehicleRequired),
        returnUnitPrice: defaultTo(0, settings?.returnUnitPrice),
        returnVatRate: defaultTo('', settings?.returnVatRate),
        returnDiscount: defaultTo(0, settings?.returnDiscount),
        returnProcessingFee: defaultTo(0, settings?.returnProcessingFee),
        returnVatValue: defaultTo(0, settings?.returnVatValue),
        returnSupplierDicountGroupCode: defaultTo('', settings?.supplierDiscountGroupCode),
        returnTotalValueWithoutVat: defaultTo(0, settings?.returnTotalValueWithoutVat),
        returnTotalValueWithVat: defaultTo(0, settings?.returnTotalValueWithVat),
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <Grid columns={GRID_COLUMNS}>
            <FormField
              control={control}
              type="text"
              name="returnTransactionType"
              label={i18n.t('entity.warehouse.labels.coreExchangeReturnTransactionType')}
              isRequired
              data-testid={suffixTestId('inputs.returnTransactionType', props)}
            />
            <FormField
              control={control}
              type="date"
              name="returnDeadline"
              label={i18n.t('entity.warehouse.labels.coreExchangeReturnDeadline')}
              isRequired
              data-testid={suffixTestId('inputs.returnDeadline', props)}
            />
            <FormField
              control={control}
              type="number"
              name="returnUnitPrice"
              label={i18n.t('entity.warehouse.labels.unitPrice')}
              decimalPlaces={PRICE_DECIMALS}
              data-testid={suffixTestId('inputs.returnUnitPrice', props)}
            />
            <FormField
              control={control}
              type="choice"
              name="returnVatRate"
              label={i18n.t('general.labels.vatRate')}
              options={vatOptions}
              isNotClearable
              isRequired
              menuInPortal
              data-testid={suffixTestId('inputs.returnVatRate', props)}
            />
            <FormField
              control={control}
              type="number"
              name="returnDiscount"
              label={i18n.t('entity.warehouse.labels.coreExchangeReturnDiscount')}
              suffix="%"
              decimalPlaces={PRICE_DECIMALS}
              data-testid={suffixTestId('inputs.returnDiscount', props)}
            />
            <FormField
              control={control}
              type="number"
              name="returnProcessingFee"
              label={i18n.t('entity.warehouse.labels.coreExchangeReturnProcessingFeeWithoutVat')}
              decimalPlaces={PRICE_DECIMALS}
              data-testid={suffixTestId('inputs.returnProcessingFee', props)}
            />
            <FormField
              control={control}
              type="number"
              name="returnVatValue"
              label={i18n.t('entity.warehouse.labels.vat')}
              decimalPlaces={PRICE_DECIMALS}
              data-testid={suffixTestId('inputs.returnVatValue', props)}
            />
            <FormField
              control={control}
              type="text"
              name="returnSupplierDicountGroupCode"
              label={i18n.t('entity.warehouse.labels.coreExchangeSupplierDiscountGroupCode')}
              data-testid={suffixTestId('inputs.returnSupplierDicountGroupCode', props)}
            />
            <FormField
              control={control}
              type="number"
              name="returnTotalValueWithoutVat"
              label={i18n.t('entity.warehouse.labels.coreExchangeReturnTotalWithoutVat')}
              decimalPlaces={PRICE_DECIMALS}
              data-testid={suffixTestId('inputs.returnTotalValueWithoutVat', props)}
            />
            <FormField
              control={control}
              type="number"
              name="returnTotalValueWithVat"
              label={i18n.t('entity.lineItems.labels.totalPriceWithVat')}
              decimalPlaces={PRICE_DECIMALS}
              data-testid={suffixTestId('inputs.returnTotalValueWithVat', props)}
            />
          </Grid>
          <FormField
            control={control}
            type="switch"
            name="returnIsVehicleRequired"
            label={i18n.t('entity.warehouse.labels.coreExchangeVehicleRequiredBySupplier')}
            data-testid={suffixTestId('inputs.returnIsVehicleRequired', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
